import { useModal } from "@/Contexts";
import ArrowDown from "@/Icons/ArrowDown";
import TrashIcon from "@/Icons/Trash";
import { ColumnType } from "@/Utils/tableColumns/functions";
import { ConfirmModal } from "@/components/ConfirmModal";

import React, { useCallback, useMemo, useState } from "react";

import { twMerge } from "tailwind-merge";

type Props = {
  values: { [key: string]: any };
  columns: ColumnType[];
  isCollapsible: boolean;
  getExpandedRowView?: (values: { [key: string]: any }) => React.ReactNode;
  rowClassName: string;
  hasDelete: boolean;
  deleteFunction: (id: string) => void;
  isLast: boolean;
};

export const TableRow: React.FC<Props> = ({
  values,
  columns,
  getExpandedRowView,
  isCollapsible,
  rowClassName,
  hasDelete,
  deleteFunction,
  isLast,
}) => {
  const [isOpen, setOpen] = useState<boolean>(false);
  const { showModal } = useModal();
  const toggleOpen = useCallback(() => {
    if (isCollapsible) {
      setOpen((prevState) => !prevState);
    }
  }, [isCollapsible, setOpen]);

  const cellsView = useMemo(() => {
    const cells = columns.map((column: ColumnType) => {
      const value = values[column.name];

      // https://react.dev/reference/react/cloneElement#caveats
      // adding key to avoid warning about non unique key
      return React.cloneElement(
        column.getView(value, values, column.onChange),
        {
          key: column.name,
        },
      );
    });

    return cells;
  }, [values, columns]);

  const onDeleteClick = useCallback(() => {
    showModal(
      <ConfirmModal
        entityName={values.name}
        onConfirm={() => deleteFunction(values.id)}
      />,
    );
  }, [deleteFunction, values, showModal]);

  return (
    <div className={twMerge("border-grey2 border-b", isLast && "border-b-0")}>
      <div
        className={twMerge(
          `relative grid min-h-[48px] grid-cols-${columns.length} hover:bg-hover-color`,
          isCollapsible && `cursor-pointer`,
          isOpen &&
            "border-grey2 bg-selected-color hover:bg-selected-color border-b",
          hasDelete && "pr-10",
          isLast && "border-b-0",
          rowClassName,
        )}
        onClick={toggleOpen}
      >
        {isCollapsible && (
          <div
            className={twMerge(
              "absolute flex h-12 items-center pl-2",
              isOpen && "rotate-180 justify-start pr-2",
              !isOpen && "justify-end",
            )}
          >
            <ArrowDown className="border-black" />
          </div>
        )}
        {cellsView}
        {hasDelete && (
          <button
            onClick={onDeleteClick}
            className="absolute top-2 right-2"
            type="button"
          >
            <TrashIcon />
          </button>
        )}
      </div>

      {isOpen &&
        isCollapsible &&
        getExpandedRowView &&
        getExpandedRowView(values)}
    </div>
  );
};
